export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  getToken: () => string | undefined;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, query, signal } = options;
    const token = this.opts.getToken();
    const base = this.opts.baseUrl ?? import.meta.env.VITE_API_URL;
    const url = buildUrl(base, path, query);

    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });

    if (res.status === 204) {
      return undefined as T;
    }

    const text = await res.text();

    if (!res.ok) {
      const parsed = text ? safeJson(text) : null;
      const payload = (parsed ?? {}) as {
        message?: string;
        code?: string;
        details?: unknown;
      };
      throw new ApiError(
        payload.message ?? res.statusText ?? 'Request failed',
        res.status,
        payload.code,
        payload.details,
      );
    }

    // 2xx without 204: we expect a JSON body. Silently returning `null` here
    // (the previous behaviour) was a footgun — callers asserted T but got
    // null, then exploded on the next field access. Treat empty or malformed
    // bodies on success as contract violations and throw, so React Query
    // surfaces them as `isError` instead of `isSuccess` with null `data`.
    if (!text) {
      throw new ApiError(
        `Server returned ${res.status} with an empty body for ${path}`,
        res.status,
      );
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiError(
        `Server returned ${res.status} with a non-JSON body for ${path}`,
        res.status,
      );
    }

    return parsed as T;
  }
}

function buildUrl(
  base: string,
  path: string,
  query: Record<string, string | number | undefined> | undefined,
): string {
  const trimmedBase = base.replace(/\/+$/, '');
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  if (!query) return `${trimmedBase}${trimmedPath}`;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${trimmedBase}${trimmedPath}?${qs}` : `${trimmedBase}${trimmedPath}`;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
